import { SHIP_MODEL_IDS, type ShipModelId } from "./shipModel";

/**
 * The booster sparks' settings: how many, where from, how they fly and how
 * they look. Each design has its own (shared defaults, plus its own changes),
 * and its craft's sparks read the one live copy, so the FX lab (fxlab.html)
 * can reshape them while they run. What's here is what ships; the lab can also
 * save its own settings for this browser's races. Distances are at the race
 * scale (a craft is 4.6 units long).
 */
export interface SparkFx {
  /** Sparks per second at top speed, all nozzles together. */
  rate: number;
  /** Sparks per second added while boosting. */
  boostRate: number;
  /** How uneven the flow is: 0 a steady stream, 1 bursts and gaps. */
  crackle: number;
  /** Where along the craft the sparks come out, from its nozzles' mouths, in
   * units: above 0 forward (into the engines), below 0 further back. Each
   * design places its own. */
  offsetZ: number;
  /** And up (above 0) or down (below 0) from the mouths' centres, in units. */
  offsetY: number;
  /** Where the flames (the jets) sit on their nozzles: along the craft (above
   * 0 forward) and up or down, in units. They move the flames only; the
   * sparks have their own. Each design places its own. */
  jetZ: number;
  jetY: number;
  /** Where across its engine's mouth a spark comes out: 0 the centre, 1 the
   * nozzle's rim. */
  startMin: number;
  startMax: number;
  /** Speed out of the nozzle, units per second. */
  speedMin: number;
  speedMax: number;
  /** Speed multiplier while boosting. */
  boostSpeed: number;
  /** Angle off straight back, in degrees: above 0 flares out, so it leaves
   * the narrowing flame and runs alongside it; below 0 closes in. */
  spreadMin: number;
  spreadMax: number;
  /** Random twist of its path around the flame's axis, ± degrees. */
  swirl: number;
  /** How far down its flame a spark gets before it dies, as a share of the
   * flame's length (0: no limit, just its lifetime). */
  reach: number;
  /** Lifetime, seconds. */
  lifeMin: number;
  lifeMax: number;
  /** Downward pull, units per second². */
  gravity: number;
  /** "streak": stretched along its flight. "dot": round. */
  shape: "streak" | "dot";
  /** Width (a dot's diameter), units. */
  widthMin: number;
  widthMax: number;
  /** A streak's length as a multiple of its width. */
  lengthMin: number;
  lengthMax: number;
  /** Width and length multipliers while boosting. */
  boostWidth: number;
  boostLength: number;
  /** The bright core's share of the width, 0–1. */
  core: number;
  /** Opacity of the glow either side of the core, 0–1. */
  glow: number;
  /** Fade-out at each end, as a share of the length, 0–0.5. */
  endFade: number;
  /** How much a streak dims from its head to its tail: 0 even, 1 to nothing. */
  tailFade: number;
  /** Colour and opacity over its life: at birth, at `midAt` (0–1), at death. */
  colorStart: string;
  colorMid: string;
  colorEnd: string;
  midAt: number;
  alphaStart: number;
  alphaMid: number;
  alphaEnd: number;
  /** "add" glows over what's behind; "alpha" paints over it. */
  blend: "add" | "alpha";
}

/** What ships: tiny, sharp, fast sparks out of the engines. Each is a
 * hard-edged streak a few pixels long from the chase camera, shot out of a
 * nozzle's mouth at 25–55 u/s, flaring a little so it runs just outside the
 * flame, and dead by the flame's tip. */
export const SPARK_DEFAULTS: Readonly<SparkFx> = {
  rate: 120,
  boostRate: 600,
  crackle: 0.7,
  offsetZ: 0,
  offsetY: 0,
  jetZ: 0,
  jetY: 0,
  startMin: 0.75,
  startMax: 1,
  speedMin: 25,
  speedMax: 55,
  boostSpeed: 1.3,
  spreadMin: 2,
  spreadMax: 7,
  swirl: 0,
  reach: 1,
  lifeMin: 0.03,
  lifeMax: 0.08,
  gravity: 6,
  shape: "streak",
  widthMin: 0.012,
  widthMax: 0.016,
  lengthMin: 2.5,
  lengthMax: 4,
  boostWidth: 1.1,
  boostLength: 1.2,
  core: 1,
  glow: 0,
  endFade: 0,
  tailFade: 0.35,
  colorStart: "#ffffff",
  colorMid: "#ffe7a0",
  colorEnd: "#ff9a3c",
  midAt: 0.5,
  alphaStart: 1,
  alphaMid: 1,
  alphaEnd: 0.2,
  blend: "add",
};

/** Each design's own settings, over the shared ones above. */
export const SPARK_BY_DESIGN: Partial<Record<ShipModelId, Partial<SparkFx>>> = {
  // Theo's, from the FX lab (29 Sep 2026); jets nudged up a little at his ask
  needle: {
    rate: 600,
    boostRate: 450,
    crackle: 0.7,
    offsetZ: 0,
    offsetY: -0.06,
    jetZ: 0,
    jetY: 0.03,
    startMin: 0.71,
    startMax: 0.77,
    speedMin: 0,
    speedMax: 51,
    boostSpeed: 1.3,
    spreadMin: 4,
    spreadMax: 3,
    swirl: 180,
    reach: 1,
    lifeMin: 0.03,
    lifeMax: 0.08,
    gravity: 6,
    shape: "streak",
    widthMin: 0.012,
    widthMax: 0.016,
    lengthMin: 1,
    lengthMax: 5,
    boostWidth: 0.8,
    boostLength: 1.2,
    core: 1,
    glow: 0.57,
    endFade: 0,
    tailFade: 0,
    colorStart: "#ffffff",
    colorMid: "#67fefc",
    colorEnd: "#000000",
    midAt: 0.5,
    alphaStart: 1,
    alphaMid: 1,
    alphaEnd: 0.2,
    blend: "add",
  },
};

/** A design's settings as the game ships them. */
export function shippedSparkFx(design: ShipModelId): SparkFx {
  return { ...SPARK_DEFAULTS, ...SPARK_BY_DESIGN[design] };
}

type ByDesign = Partial<Record<ShipModelId, Partial<SparkFx>>>;

// v3 (29 Sep 2026): Theo's Needle became what ships; the lab starts afresh
const STORE_KEY = "driftracer.sparkFx.v3";

interface Stored {
  /** Race with these instead of the game's. */
  use: boolean;
  ships: ByDesign;
}

/** Keep only known keys with values of the right type. */
export function cleanSparkFx(raw: unknown): Partial<SparkFx> {
  const out: Record<string, unknown> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!(k in SPARK_DEFAULTS)) continue;
    const d = (SPARK_DEFAULTS as unknown as Record<string, unknown>)[k];
    if (typeof d === "number" && typeof v === "number" && Number.isFinite(v)) out[k] = v;
    else if (k === "shape" && (v === "streak" || v === "dot")) out[k] = v;
    else if (k === "blend" && (v === "add" || v === "alpha")) out[k] = v;
    else if (typeof d === "string" && typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v)) out[k] = v;
  }
  return out as Partial<SparkFx>;
}

/** Settings for several designs ({ "needle": {...}, "brute": {...} }), cleaned;
 * anything that isn't a design is dropped. */
export function cleanSparkFxByDesign(raw: unknown): ByDesign {
  const out: ByDesign = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if ((SHIP_MODEL_IDS as string[]).includes(k)) out[k as ShipModelId] = cleanSparkFx(v);
  }
  return out;
}

/** The lab's saved settings for this browser, if any. */
export function loadStoredSparkFx(): Stored | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as { use?: unknown; ships?: unknown; fx?: unknown };
    const ships = cleanSparkFxByDesign(s.ships);
    // the lab's first version saved one set for every craft
    if (!s.ships && s.fx) for (const d of SHIP_MODEL_IDS) ships[d] = cleanSparkFx(s.fx);
    return { use: s.use === true, ships };
  } catch {
    return null;
  }
}

/** Save every design's live settings for this browser; `use` makes its races
 * use them. */
export function storeSparkFx(use: boolean): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ use, ships: Object.fromEntries(live) }));
  } catch {
    /* storage blocked: the settings just won't persist */
  }
}

const stored = loadStoredSparkFx();
const live = new Map<ShipModelId, SparkFx>();

/** A design's live settings, which its craft's sparks read: as shipped, or
 * this browser's own if the lab says to race with them. */
export function sparkFxFor(design: ShipModelId): SparkFx {
  let fx = live.get(design);
  if (!fx) {
    fx = { ...shippedSparkFx(design), ...(stored?.use ? stored.ships[design] : {}) };
    live.set(design, fx);
  }
  return fx;
}

const listeners = new Set<(design: ShipModelId) => void>();

/** Change a design's live settings; its craft's sparks follow at once. */
export function setSparkFx(design: ShipModelId, next: Partial<SparkFx>): void {
  Object.assign(sparkFxFor(design), next);
  for (const f of listeners) f(design);
}

/** Be told which design's look changed. Returns an unsubscribe. */
export function onSparkFxChange(f: (design: ShipModelId) => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}
